import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { LuX, LuPlus } from 'react-icons/lu';
import api from '../../services/api';
import { FRANJAS_BLOQUEO, DIAS_SEMANA, labelFranja } from '../../utils/bloqueos';
import { fechaAR, getTodayISO } from '../../utils/dateUtils';

// "Mis días ocupados" (5-oct-2026): el técnico/socio marca cuándo trabaja en lo
// suyo. El admin lo ve como "ocupado" al asignar visitas (nunca ve la nota).
const chip = (on) => `h-9 px-3 rounded-xl text-label font-black active:scale-95 ${on ? 'bg-brand-red text-white' : 'bg-chip text-muted'}`;

export default function MisDiasOcupados() {
    const [lista, setLista] = useState([]);
    const [abierto, setAbierto] = useState(false);
    const [modo, setModo] = useState('SEMANAL'); // SEMANAL | DIA
    const [dia, setDia] = useState(1);
    const [fecha, setFecha] = useState(getTodayISO());
    // Franjas de 2 h (20-oct-2026): se pueden marcar varias; "Todo el día" y "Horario" van solas
    const [franjas, setFranjas] = useState(['DIA']);
    const franja = franjas[0];
    const tocarFranja = (id) => setFranjas(fs => {
        if (id === 'DIA' || id === 'HORARIO') return [id];
        const sin = fs.filter(x => x !== 'DIA' && x !== 'HORARIO');
        const nuevas = sin.includes(id) ? sin.filter(x => x !== id) : [...sin, id];
        return nuevas.length ? nuevas : ['DIA'];
    });
    const [desde, setDesde] = useState('09:00');
    const [hasta, setHasta] = useState('13:00');
    const [nota, setNota] = useState('');

    const cargar = () => api.get('/bloqueos/mios').then(r => setLista(r.data || [])).catch(() => setLista([]));
    useEffect(() => { cargar(); }, []);

    const guardar = async () => {
        // Horario (18-oct-2026): "el martes a la mañana estoy libre" → ocupado de 13 a 20
        if (franja === 'HORARIO' && (!desde || !hasta || desde >= hasta)) { toast.error('El horario tiene que terminar después de empezar'); return; }
        try {
            const cuando = modo === 'SEMANAL' ? { diaSemana: dia } : { fecha };
            for (const f of franjas) {
                await api.post('/bloqueos', { franja: f === 'HORARIO' ? `${desde}-${hasta}` : f, nota, ...cuando });
            }
            toast.success('Listo, el admin ya lo ve como ocupado');
            setAbierto(false); setNota(''); cargar();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); }
    };
    const borrar = async (id) => {
        try { await api.delete(`/bloqueos/${id}`); cargar(); } catch { toast.error('No se pudo borrar'); }
    };

    return (
        <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <p className="text-body font-black text-ink">Mis días ocupados</p>
                    <p className="text-caption text-muted">Cuando trabajás en lo tuyo. El admin solo ve "ocupado".</p>
                </div>
                {!abierto && (
                    <button onClick={() => setAbierto(true)} className="h-9 px-3 rounded-xl bg-brand-red text-white text-label font-black flex items-center gap-1 active:scale-95 shrink-0">
                        <LuPlus size={14} /> Marcar
                    </button>
                )}
            </div>

            {lista.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {lista.map(b => (
                        <span key={b.id} className="inline-flex items-center gap-1.5 pl-3 pr-1 h-8 rounded-full bg-chip text-caption font-bold text-ink">
                            {b.diaSemana ? `Todos los ${DIAS_SEMANA[b.diaSemana].toLowerCase()}` : fechaAR(b.fecha)} · {labelFranja(b.franja)}
                            {b.nota && <span className="text-muted font-medium truncate max-w-[110px]">· {b.nota}</span>}
                            <button onClick={() => borrar(b.id)} aria-label="Quitar" className="w-6 h-6 rounded-full flex items-center justify-center text-muted active:bg-card"><LuX size={13} /></button>
                        </span>
                    ))}
                </div>
            )}

            {abierto && (
                <div className="space-y-3 pt-1">
                    <div className="flex gap-2">
                        <button onClick={() => setModo('SEMANAL')} className={chip(modo === 'SEMANAL')}>Todas las semanas</button>
                        <button onClick={() => setModo('DIA')} className={chip(modo === 'DIA')}>Un día</button>
                    </div>
                    {modo === 'SEMANAL' ? (
                        <div className="flex flex-wrap gap-1.5">
                            {DIAS_SEMANA.slice(1, 7).map((n, i) => (
                                <button key={n} onClick={() => setDia(i + 1)} className={chip(dia === i + 1)}>{n.slice(0, 3)}</button>
                            ))}
                        </div>
                    ) : (
                        <input type="date" value={fecha} min={getTodayISO()} onChange={e => setFecha(e.target.value)}
                            className="h-10 px-3 rounded-xl bg-chip text-ink text-body font-bold outline-none" />
                    )}
                    <div className="flex flex-wrap gap-1.5">
                        {FRANJAS_BLOQUEO.map(f => <button key={f.id} onClick={() => tocarFranja(f.id)} aria-pressed={franjas.includes(f.id)} className={chip(franjas.includes(f.id))}>{f.label}</button>)}
                        <button onClick={() => tocarFranja('HORARIO')} aria-pressed={franja === 'HORARIO'} className={chip(franja === 'HORARIO')}>Otro horario</button>
                    </div>
                    {franja === 'HORARIO' && (
                        <div className="flex items-center gap-2">
                            <span className="text-caption font-bold text-muted">Ocupado de</span>
                            <input type="time" value={desde} onChange={e => setDesde(e.target.value)} step={900}
                                className="h-10 px-2 rounded-xl bg-chip text-ink text-body font-bold outline-none" aria-label="Desde" />
                            <span className="text-caption font-bold text-muted">a</span>
                            <input type="time" value={hasta} onChange={e => setHasta(e.target.value)} step={900}
                                className="h-10 px-2 rounded-xl bg-chip text-ink text-body font-bold outline-none" aria-label="Hasta" />
                        </div>
                    )}
                    <p className="text-label text-muted">Marcá cuándo NO podés: una o varias franjas (ej. «12 a 14»), todo el día u otro horario.</p>
                    <input value={nota} onChange={e => setNota(e.target.value)} placeholder="Nota para vos (opcional, el admin no la ve)"
                        className="w-full h-10 px-3 rounded-xl bg-chip text-ink text-body outline-none placeholder:text-muted" />
                    <div className="flex gap-2">
                        <button onClick={() => setAbierto(false)} className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black uppercase">Cancelar</button>
                        <button onClick={guardar} className="flex-[2] h-10 rounded-xl bg-brand-red text-white text-label font-black uppercase">Guardar</button>
                    </div>
                </div>
            )}
        </div>
    );
}
